import { memo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addViewedItemState } from 'redux_store';
// Row-state tints (`.hoverNewRow`, `.hoverViewedRow`, error variants)
// live in this stylesheet and are toggled via classList from the
// IntersectionObserver below. The FSD `tableBodyProp` mirrors the same
// `.hoverNewRow td { background: var(--qhs-row-new) }` rules at the JS
// level so legacy and FSD callers paint identical row tints. We keep
// the side-effect import here so an FSD-only page still pulls the
// stylesheet onto the document.
import '../legacy-row-state.css';

/**
 * Wrapper used by `TableCell` when a column has `hasManageViewed=true`
 * (Alerts table — the operator sees fresh / viewed-error rows). It
 * watches the cell intersect the viewport and dispatches
 * `addViewedItemState(uuid)` once per row, then mutates the parent
 * `<tr>` classList to swap `hoverNewRow*` for `hoverViewedRow*`.
 *
 * Lifted from `_legacy/components/Shared/Table/widgets/Cell/
 * CellContent/ViewedTableCell.jsx`. Behaviour is preserved 1:1
 * (same dispatch on first intersection, same classList swap).
 *
 * Two perf-correctness fixes vs the legacy version:
 *   - `observer` and `sent` are useRefs, not local `let`s, so the
 *     cleanup useEffect actually disconnects the observer it created
 *     (the legacy `let observer` reset to `undefined` on every render
 *     and the empty-deps cleanup captured the first-render value,
 *     leaking one IntersectionObserver per re-render across the entire
 *     Alerts table on every Redux `viewState` update);
 *   - the Redux selector is narrowed to `state.table.viewState` so a
 *     column resize / sort / filter elsewhere on the page no longer
 *     wakes every visible Viewed cell.
 */
const selectViewState = state => state.table.viewState || {};

const ViewedTableCell = memo(function ViewedTableCell({ children, row, canManageViewed }) {
  const viewState = useSelector(selectViewState);
  const dispatch = useDispatch();
  const { notViewedUuids = [], wasSentViewedUuids = [] } = viewState;
  const ref = useRef(null);
  const observerRef = useRef(null);
  const sentRef = useRef(false);

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const node = ref.current?.parentElement?.parentElement;
    if (!node) return;

    if (wasSentViewedUuids.includes(row?.uuid)) {
      node.classList.remove(row?.errorType === true ? 'hoverNewErrorRow' : 'hoverNewRow');
      node.classList.add(row?.errorType === true ? 'hoverViewedErrorRow' : 'hoverViewedRow');
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    if (notViewedUuids.includes(row?.uuid)) {
      node.classList.add(row?.errorType === true ? 'hoverNewErrorRow' : 'hoverNewRow');
      if (!observerRef.current && canManageViewed) {
        const observer = new IntersectionObserver((entries, _observer) => {
          entries.forEach(entry => {
            if (entry.intersectionRatio > 0 && !sentRef.current) {
              const uuid = row?.uuid;
              if (uuid && !wasSentViewedUuids.includes(uuid)) {
                dispatch(addViewedItemState(uuid));
              }
              _observer.disconnect();
              observerRef.current = null;
              sentRef.current = true;
            }
          });
        });
        observer.observe(node);
        observerRef.current = observer;
      }
    }
  }, [notViewedUuids, wasSentViewedUuids, canManageViewed, row?.uuid, row?.errorType, dispatch]);

  return (
    <div ref={ref} style={{ maxWidth: '100%', width: '100%' }}>
      {children}
    </div>
  );
});

export default ViewedTableCell;
